import { useEffect, useState } from "react";
import { AuthTokenRetrieve, LoginButton } from "../Utility/AuthTokenRetrieve";
import { buildApiUrl } from "../Utility/api";
import "../Food/RecipeList.css";
import "./FoodDiary.css";
import { fetchDiarySymptoms } from "./FoodDiaryShared";
import { defaultSymptomForm } from "./FoodDiaryShared";

const FoodDiarySymptoms = () => {
  const [shouldShow, setShouldShow] = useState();
  const [symptoms, setSymptoms] = useState([]);
  const [selectedSymptom, setSelectedSymptom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [editingSymptom, setEditingSymptom] = useState(false);
  const [symptomForm, setSymptomForm] = useState(defaultSymptomForm());
  const { shouldShow: shouldShowGoogle } = AuthTokenRetrieve();

  useEffect(() => {
    setShouldShow(shouldShowGoogle);
  }, [shouldShowGoogle]);

  const accessToken = localStorage.getItem("lesterAccessToken") || "";

  useEffect(() => {
    if (!shouldShow || !accessToken) {
      setLoading(false);
      return;
    }

    setLoading(true);
    fetchDiarySymptoms(accessToken)
      .then((symptomData) => {
        setSymptoms(symptomData || []);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [shouldShow, accessToken]);

  const loadSymptomDetail = (symptomId) => {
    setLoadingDetail(true);
    setStatusMessage("");
    fetch(
      buildApiUrl(`/food/diary/symptoms/${symptomId}?accessToken=${encodeURIComponent(accessToken)}`),
      { cache: "no-cache" }
    )
      .then((response) => response.json())
      .then((symptomDetail) => {
        setSelectedSymptom(symptomDetail);
        setEditingSymptom(false);
        setLoadingDetail(false);
      })
      .catch(() => {
        setLoadingDetail(false);
      });
  };

  const toLocalDateTimeInput = (value) => {
    if (!value) {
      return "";
    }

    const [datePart, timePart, meridiem] = value.split(" ");
    if (!datePart || !timePart || !meridiem) {
      return "";
    }

    const [month, day, year] = datePart.split("/");
    let [hours, minutes] = timePart.split(":");
    let parsedHours = Number(hours);
    if (meridiem === "PM" && parsedHours < 12) {
      parsedHours += 12;
    }
    if (meridiem === "AM" && parsedHours === 12) {
      parsedHours = 0;
    }

    return `${year}-${month}-${day}T${String(parsedHours).padStart(2, "0")}:${minutes}`;
  };

  const beginSymptomEdit = () => {
    if (!selectedSymptom) {
      return;
    }

    setSymptomForm({
      startedAt: toLocalDateTimeInput(selectedSymptom.startedAt),
      endedAt: toLocalDateTimeInput(selectedSymptom.endedAt),
      symptomType: selectedSymptom.symptomType || "Other",
      severity: String(selectedSymptom.severity ?? "5"),
      notes: selectedSymptom.notes || "",
    });
    setEditingSymptom(true);
    setStatusMessage("");
  };

  const saveSymptom = () => {
    fetch(buildApiUrl(`/food/diary/symptoms/${selectedSymptom.id}`), {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(symptomForm),
    })
      .then((response) => response.json())
      .then(() => {
        setEditingSymptom(false);
        setStatusMessage("Symptom updated.");
        fetchDiarySymptoms(accessToken).then((symptomData) => {
          setSymptoms(symptomData || []);
        });
        loadSymptomDetail(selectedSymptom.id);
      });
  };

  const deleteSymptom = () => {
    fetch(buildApiUrl(`/food/diary/symptoms/${selectedSymptom.id}`), {
      method: "DELETE",
    })
      .then((response) => response.json())
      .then(() => {
        setSelectedSymptom(null);
        setStatusMessage("Symptom deleted.");
        fetchDiarySymptoms(accessToken).then((symptomData) => {
          setSymptoms(symptomData || []);
        });
      });
  };

  if (!shouldShow) {
    return (
      <div className="searchBarDiv formViewWrapper FoodDiaryLogin">
        <h2>Food diary</h2>
        <p>Sign in to view private symptom history.</p>
        <LoginButton setShouldShow={setShouldShow}></LoginButton>
      </div>
    );
  }

  return (
    <div className="FoodDiaryPage">
      <div className="FoodDiaryGrid">
        <div className="shoppingListDiv FoodDiaryPanel">
          <h2 className="FoodDiaryTitle">Symptom history</h2>
          <p className="FoodDiarySubtitle">Recent symptoms are shown in Central Time.</p>
          {loading ? (
            <p>Loading symptoms...</p>
          ) : symptoms.length ? (
            symptoms.map((symptom) => (
              <button
                key={symptom.id}
                className={`FoodDiaryHistoryButton ${selectedSymptom?.id === symptom.id ? "FoodDiaryHistoryButtonSelected" : ""}`}
                onClick={() => loadSymptomDetail(symptom.id)}
              >
                <div>
                  <strong>{symptom.symptomType}</strong>
                  <div>{symptom.startedAt}</div>
                </div>
                <div className="FoodDiaryHistoryMeta">
                  <span>Severity {symptom.severity}</span>
                  <span>{symptom.endedAt ? "Ended" : "Active"}</span>
                </div>
              </button>
            ))
          ) : (
            <p>No symptoms logged yet.</p>
          )}
        </div>

        <div className="shoppingListDiv FoodDiaryPanel">
          <h2 className="FoodDiaryTitle">Symptom detail</h2>
          {statusMessage ? <p className="FoodDiaryStatus">{statusMessage}</p> : null}
          {loadingDetail ? <p>Loading symptom detail...</p> : null}
          {!loadingDetail && !selectedSymptom ? (
            <p>Select a symptom to inspect its timing and notes.</p>
          ) : null}
          {selectedSymptom ? (
            <div className="FoodDiaryMealDetail">
              {editingSymptom ? (
                <div className="FoodDiaryEditBlock">
                  <div className="FoodDiaryTwoColumn">
                    <div className="inputDiv">
                      <label>Started</label>
                      <input
                        type="datetime-local"
                        className="inputBox addInput"
                        value={symptomForm.startedAt}
                        onChange={(event) => setSymptomForm((currentValue) => ({ ...currentValue, startedAt: event.target.value }))}
                      />
                    </div>
                    <div className="inputDiv">
                      <label>Ended</label>
                      <input
                        type="datetime-local"
                        className="inputBox addInput"
                        value={symptomForm.endedAt}
                        onChange={(event) => setSymptomForm((currentValue) => ({ ...currentValue, endedAt: event.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="FoodDiaryTwoColumn">
                    <div className="inputDiv">
                      <label>Symptom</label>
                      <input
                        className="inputBox addInput"
                        value={symptomForm.symptomType}
                        onChange={(event) => setSymptomForm((currentValue) => ({ ...currentValue, symptomType: event.target.value }))}
                      />
                    </div>
                    <div className="inputDiv">
                      <label>Severity</label>
                      <input
                        className="inputBox addInput"
                        value={symptomForm.severity}
                        onChange={(event) => setSymptomForm((currentValue) => ({ ...currentValue, severity: event.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="inputDiv">
                    <label>Notes</label>
                    <textarea
                      className="inputBox textAreaAdd FoodDiaryTextarea"
                      value={symptomForm.notes}
                      onChange={(event) => setSymptomForm((currentValue) => ({ ...currentValue, notes: event.target.value }))}
                    />
                  </div>
                  <div className="FoodDiaryActionRow">
                    <button className="summaryButton" onClick={saveSymptom}>Save</button>
                    <button className="summaryButton" onClick={() => setEditingSymptom(false)}>Cancel</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="FoodDiaryMealDetailHeader">
                    <strong>{selectedSymptom.symptomType}</strong>
                    <span>Severity {selectedSymptom.severity}</span>
                  </div>
                  <div className="FoodDiarySymptomMeta">
                    <span>Started: {selectedSymptom.startedAt}</span>
                    <span>Ended: {selectedSymptom.endedAt || "Still active"}</span>
                  </div>
                  <div className="FoodDiaryActionRow">
                    <button className="summaryButton" onClick={beginSymptomEdit}>Edit Symptom</button>
                    <button className="summaryButton" onClick={deleteSymptom}>Delete Symptom</button>
                  </div>
                  {selectedSymptom.notes ? (
                    <div className="FoodDiaryDetailBlock">
                      <strong>Notes</strong>
                      <p className="FoodDiaryMealNotes">{selectedSymptom.notes}</p>
                    </div>
                  ) : (
                    <div className="FoodDiaryDetailBlock">
                      <strong>Notes</strong>
                      <p className="FoodDiaryMealNotes">No notes were added for this symptom.</p>
                    </div>
                  )}
                </>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default FoodDiarySymptoms;
